/**
 * Checkbox Component
 * Checkbox input using Radix UI
 * 
 * @author Varun Pandey
 */

import React, { useRef } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface CheckboxProps {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label?: string;
  /** Applies to the Radix checkbox root (the square box). */
  checkboxClassName?: string;
  className?: string;
  disabled?: boolean;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onCheckedChange,
  label,
  checkboxClassName,
  className,
  disabled,
}) => {
  const rootRef = useRef<HTMLButtonElement>(null);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <CheckboxPrimitive.Root
        ref={rootRef}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        className={cn(
          'peer h-5 w-5 shrink-0 rounded-md border-2 border-[#3A3A3C] bg-[#2C2C2E]',
          'cursor-pointer',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:bg-[#F33363] data-[state=checked]:border-[#F33363]',
          'transition-colors',
          checkboxClassName
        )}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-white">
          <Check className="h-4 w-4" />
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
      {label && (
        <label
          className="text-sm font-medium text-white uppercase tracking-wide cursor-pointer select-none opacity-70"
          onClick={(e) => {
            if (disabled) return;
            e.preventDefault();
            rootRef.current?.click();
          }}
        >
          {label}
        </label>
      )}
    </div>
  );
};
